/**
 * Why does a family day have nothing to order? Classifies the empty day into
 * a real-world reason (school break, menu not posted yet, holiday, menu still
 * being authored, …) so the day view can explain instead of showing a generic
 * "no menu this day".
 *
 * Inputs are the menus the server returned for the date (it returns every
 * menu spanning the date, archived included — the client filters) plus the
 * `dayContext` aggregates from `GET /api/menus?activeOn=…`.
 */

import { dateFromKey } from '../school-orders/calendar'

export type NoMenuReason =
  /**
   * The school isn't serving this date: a term-calendar NO_SERVICE day, a date
   * in a menu's blockedDates, or a weekday switched off on the menu's service
   * days (`MenuServiceLine.serviceDays`).
   */
  | { kind: 'no-service-day'; notes: string | null }
  /** A menu spans the date but every menu spanning it is archived. */
  | { kind: 'menu-pulled' }
  /** An active menu spans the date but has zero visible items. */
  | { kind: 'menu-empty' }
  /** The date is before the school's first-ever menu (school already live). */
  | { kind: 'before-first-menu'; firstMenuStartDate: string }
  /** Long gap between menus (≥ a week) — reads as a school break. */
  | { kind: 'school-break'; nextMenuStartDate: string }
  /** Short gap between menus — a one-off day/week without service. */
  | { kind: 'gap-day'; nextMenuStartDate: string }
  /** Menus exist in the past but nothing covers this date or later. */
  | { kind: 'not-posted-yet' }
  /** The school has never had a menu. */
  | { kind: 'not-set-up' }
  /**
   * The student's grade has no menu-band mapping, so the fail-closed
   * grade-band gate (2026-07 flip) hides every menu from them — including
   * untagged ones — even while siblings order fine.
   */
  | { kind: 'grade-band-unmapped'; studentName: string }
  | { kind: 'unknown' }

export interface NoMenuReasonMenu {
  isArchived: boolean
  blockedOnDate?: boolean
  /** The date's weekday is switched off on the menu's service days. */
  outsideServiceDays?: boolean
  items: ReadonlyArray<{ visible: boolean; isArchived: boolean }>
}

export interface NoMenuDayContext {
  firstMenuStartDate: string | null
  lastMenuEndDate: string | null
  nextMenuStartDate: string | null
  prevMenuEndDate: string | null
  noServiceDay: { notes: string | null } | null
}

/** Gaps at least this long read as "school's on break" rather than a gap day. */
const BREAK_MIN_DAYS = 7

/** Whole days from key `a` to key `b` (positive when `b` is after `a`). */
export function daysBetweenKeys(a: string, b: string): number {
  return Math.round((dateFromKey(b).getTime() - dateFromKey(a).getTime()) / 86_400_000)
}

export function resolveNoMenuReason(args: {
  /** The viewed day, `YYYY-MM-DD`. */
  date: string
  /** Today, `YYYY-MM-DD` — distinguishes browsing the past from pre-launch. */
  today: string
  menus: ReadonlyArray<NoMenuReasonMenu>
  dayContext: NoMenuDayContext | null | undefined
}): NoMenuReason {
  const ctx = args.dayContext ?? null
  if (ctx?.noServiceDay) {
    return { kind: 'no-service-day', notes: ctx.noServiceDay.notes }
  }

  const hasVisibleItems = (m: NoMenuReasonMenu): boolean => m.items.some((it) => it.visible && !it.isArchived)
  const active = args.menus.filter((m) => !m.isArchived)
  // Not served this date: a one-off calendar exception (`blockedOnDate`) or the
  // weekday being switched off on the menu's service days
  // (`outsideServiceDays`). Both mean "the school isn't serving", and the
  // server has already emptied such a menu's item list — so they must be
  // classified BEFORE the itemless-menu branch below, or a school that simply
  // doesn't serve Wednesdays reads as a menu nobody finished authoring.
  const notServed = (m: NoMenuReasonMenu): boolean => Boolean(m.blockedOnDate) || Boolean(m.outsideServiceDays)
  const served = active.filter((m) => !notServed(m))
  if (served.some(hasVisibleItems)) {
    // The day has orderable food — callers shouldn't be asking.
    return { kind: 'unknown' }
  }
  if (active.some(notServed)) {
    return { kind: 'no-service-day', notes: null }
  }
  if (served.length > 0) {
    return { kind: 'menu-empty' }
  }
  if (args.menus.length > 0) {
    return { kind: 'menu-pulled' }
  }

  // No menu spans the date at all — classify by where it sits in history.
  if (!ctx) {
    return { kind: 'unknown' }
  }
  if (ctx.firstMenuStartDate === null) {
    return { kind: 'not-set-up' }
  }
  if (ctx.nextMenuStartDate) {
    if (args.date < ctx.firstMenuStartDate && ctx.firstMenuStartDate <= args.today) {
      return { kind: 'before-first-menu', firstMenuStartDate: ctx.firstMenuStartDate }
    }
    // Break vs one-off gap: measure the full hole between the surrounding
    // menus (not distance from the viewed day — a parent landing on the last
    // day of a three-week break is still on break).
    const gapDays = ctx.prevMenuEndDate
      ? daysBetweenKeys(ctx.prevMenuEndDate, ctx.nextMenuStartDate)
      : daysBetweenKeys(args.date, ctx.nextMenuStartDate)
    return gapDays >= BREAK_MIN_DAYS
      ? { kind: 'school-break', nextMenuStartDate: ctx.nextMenuStartDate }
      : { kind: 'gap-day', nextMenuStartDate: ctx.nextMenuStartDate }
  }
  return { kind: 'not-posted-yet' }
}

// ---------------------------------------------------------------------------
// Grade-band gate (2026-07 fail-closed flip): a STUDENT whose grade has no
// explicit GradeRange mapping is blocked from every menu — untagged ones
// included — on the gated surfaces. Without a dedicated reason the parent
// sees a generic "no meals" that is indistinguishable from a real menu gap
// and files a support ticket. These helpers turn that dead end into a
// self-diagnosing message. Staff self-diners (TEACHER memberships) are
// band-less BY DESIGN and are never classified as blocked here.
// ---------------------------------------------------------------------------

/** The per-(student, campus) inputs the band-blocked check reads. */
export interface BandGateStudent {
  /** Shown in the message. Never "You" in practice — self-diners aren't band-gated. */
  displayName: string
  /** Household owner ordering for themselves — a staff self-diner, band-less by design. */
  isSelf: boolean
  /**
   * The diner's roster row at the campus being viewed; null = not rostered
   * there (a gated student with nothing mapped, mirroring the server's
   * fail-closed rule).
   */
  location: {
    /** Explicit school grade→band match ONLY; null = unmapped (blocked when gated). */
    gradeRangeCode: string | null
    /**
     * Server-mirrored "holds a STUDENT membership at this campus" flag
     * (lands with the parent-day band gate, PR #1173). Older wire shapes
     * omit it — fall back to `!isSelf`: non-self household members are
     * students, the self-diner is staff.
     */
    isStudent?: boolean
  } | null
}

/** True when the fail-closed grade-band gate hides every menu from this diner. */
export function isBandBlockedStudent(student: BandGateStudent): boolean {
  const bandGated = student.location?.isStudent ?? !student.isSelf
  return bandGated && (student.location?.gradeRangeCode ?? null) === null
}

/**
 * Compact one-line variant of the band-gate hint, for the week grid's
 * per-child rows (the day view renders the richer `grade-band-unmapped`
 * empty state instead). Keep the two in the same voice when editing copy.
 */
export function gradeBandUnmappedMessage(studentName: string): string {
  return `Menus for ${studentName} aren't available yet — their grade needs to be set up by the school.`
}

/**
 * Per-student wrapper over `resolveNoMenuReason`: when the day-level
 * classifier has no explanation (`unknown`) and the student is blocked by
 * the fail-closed grade-band gate, that gate IS the explanation — most
 * notably a day whose menus are visible to everyone except this student.
 * Day-level reasons (break, holiday, not posted…) still win: they are true
 * for every child, band or not.
 */
export function resolveStudentNoMenuReason(args: {
  /** The viewed day, `YYYY-MM-DD`. */
  date: string
  /** Today, `YYYY-MM-DD` — distinguishes browsing the past from pre-launch. */
  today: string
  menus: ReadonlyArray<NoMenuReasonMenu>
  dayContext: NoMenuDayContext | null | undefined
  /** The child whose tab is asking; null when no child context applies. */
  student: BandGateStudent | null
}): NoMenuReason {
  const base = resolveNoMenuReason(args)
  if (base.kind === 'unknown' && args.student && isBandBlockedStudent(args.student)) {
    return { kind: 'grade-band-unmapped', studentName: args.student.displayName }
  }
  return base
}
